<template>
  <table
    role="grid"
    :aria-label="t('el.datepicker.quarterTablePrompt')"
    :class="ns.b()"
    @click="handleQuarterTableClick"
    @mousemove="handleMouseMove"
  >
    <tbody ref="tbodyRef">
      <tr>
        <td
          v-for="(cell, key_) in rows"
          :key="key_"
          :ref="(el) => isSelectedCell(cell) && (currentCellRef = el as HTMLElement)"
          :class="getCellStyle(cell)"
          :aria-selected="`${isSelectedCell(cell)}`"
          :aria-label="t(`el.datepicker.quarter${cell.text}`)"
          :tabindex="isSelectedCell(cell) ? 0 : -1"
          @keydown.space.prevent.stop="handleQuarterTableClick"
          @keydown.enter.prevent.stop="handleQuarterTableClick"
        >
          <div>
            <span class="cell">
              {{ t('el.datepicker.quarter.' + quarters[cell.text - 1]) }}
            </span>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import dayjs from 'dayjs'
import { useLocale, useNamespace } from '@jdesign-vue/hooks'
import { rangeArr } from '@jdesign-vue/components/time-picker'
import { castArray, hasClass } from '@jdesign-vue/utils'
import { basicMonthTableProps } from '../props/basic-month-table'

type QuarterCell = {
  column: number
  row: number
  disabled: boolean
  start: boolean
  end: boolean
  text: number
  type: 'normal' | 'today'
  inRange: boolean
}

const datesInQuarter = (year: number, quarter: number, lang: string) => {
  const firstDay = dayjs()
    .locale(lang)
    .year(year)
    .quarter(quarter)
    .startOf('quarter')
  const lastDay = dayjs()
    .locale(lang)
    .year(year)
    .quarter(quarter + 1)
    .startOf('quarter')
  const numOfDays = lastDay.diff(firstDay, 'day')
  return rangeArr(numOfDays).map((n) => firstDay.add(n, 'day').toDate())
}

const props = defineProps(basicMonthTableProps)
const emit = defineEmits(['changerange', 'pick', 'select'])

const ns = useNamespace('quarter-table')

const { t, lang } = useLocale()
const tbodyRef = ref<HTMLElement>()
const currentCellRef = ref<HTMLElement>()
const quarters = ref(['q1', 'q2', 'q3', 'q4'])
const tableRows = ref<QuarterCell[]>([])
const lastRow = ref<number>()
const lastColumn = ref<number>()
const rows = computed<QuarterCell[]>(() => {
  const rows = tableRows.value

  const now = dayjs().locale(lang.value).startOf('quarter')

  for (let j = 0; j < 4; j++) {
    const cell = (rows[j] ||= {
      row: 0,
      column: j,
      type: 'normal',
      inRange: false,
      start: false,
      end: false,
      text: j + 1,
      disabled: false,
    })
    cell.type = 'normal'
    const index = j
    const calTime = props.date.startOf('year').quarter(index + 1)
    const calEndDate =
      props.rangeState.endDate ||
      props.maxDate ||
      (props.rangeState.selecting && props.minDate) ||
      null
    cell.inRange = !!(
      (props.minDate &&
        (calTime.isSame(props.minDate, 'quarter') ||
          calTime.isAfter(props.minDate, 'quarter')) &&
        calEndDate &&
        (calTime.isSame(calEndDate, 'quarter') ||
          calTime.isBefore(calEndDate, 'quarter'))) ||
      (props.minDate &&
        (calTime.isSame(props.minDate, 'quarter') ||
          calTime.isBefore(props.minDate, 'quarter')) &&
        calEndDate &&
        (calTime.isSame(calEndDate, 'quarter') ||
          calTime.isAfter(calEndDate, 'quarter')))
    )
    if (
      props.minDate &&
      (props.minDate.isSame(calEndDate, 'quarter') ||
        props.minDate.isAfter(calEndDate, 'quarter'))
    ) {
      cell.start = !!(calEndDate && calTime.isSame(calEndDate, 'quarter'))
      cell.end = !!(props.minDate && calTime.isSame(props.minDate, 'quarter'))
    } else {
      cell.start = !!(props.minDate && calTime.isSame(props.minDate, 'quarter'))
      cell.end = !!(calEndDate && calTime.isSame(calEndDate, 'quarter'))
    }

    const isToday = now.isSame(calTime)
    if (isToday) {
      cell.type = 'today'
    }

    cell.text = index + 1
    cell.disabled = props.disabledDate?.(calTime.toDate()) || false
  }
  return rows
})

const focus = () => {
  currentCellRef.value?.focus()
}

const getCellStyle = (cell: QuarterCell) => {
  const style = {} as any
  const year = props.date.year()
  const today = new Date()
  const quarter = cell.text

  style.disabled = props.disabledDate
    ? datesInQuarter(year, quarter, lang.value).every(props.disabledDate)
    : false
  style.current =
    castArray(props.parsedValue).findIndex(
      (date) =>
        dayjs.isDayjs(date) &&
        date.year() === year &&
        date.quarter() === quarter
    ) >= 0
  style.today =
    dayjs(today).year() === year && dayjs(today).quarter() === quarter

  if (cell.inRange) {
    style['in-range'] = true

    if (cell.start) {
      style['start-date'] = true
    }

    if (cell.end) {
      style['end-date'] = true
    }
  }
  return style
}

const isSelectedCell = (cell: QuarterCell) => {
  const year = props.date.year()
  const quarter = cell.text
  return (
    castArray(props.date).findIndex(
      (date) => date.year() === year && date.quarter() === quarter
    ) >= 0
  )
}

const handleMouseMove = (event: MouseEvent) => {
  if (!props.rangeState.selecting) return

  let target = event.target as HTMLElement
  if (target.tagName === 'SPAN') {
    target = target.parentNode?.parentNode as HTMLElement
  }
  if (target.tagName === 'DIV') {
    target = target.parentNode as HTMLElement
  }
  if (target.tagName !== 'TD') return

  const row = (target.parentNode as HTMLTableRowElement).rowIndex
  const column = (target as HTMLTableCellElement).cellIndex
  // can not select disabled date
  if (rows.value[column].disabled) return

  // only update rangeState when mouse moves to a new cell
  // this avoids frequent Date object creation and improves performance
  if (row !== lastRow.value || column !== lastColumn.value) {
    lastRow.value = row
    lastColumn.value = column
    emit('changerange', {
      selecting: true,
      endDate: props.date.startOf('year').quarter(column + 1),
    })
  }
}
const handleQuarterTableClick = (event: MouseEvent | KeyboardEvent) => {
  const target = (event.target as HTMLElement)?.closest(
    'td'
  ) as HTMLTableCellElement
  if (target?.tagName !== 'TD') return
  if (hasClass(target, 'disabled')) return
  const column = target.cellIndex
  const quarter = column + 1
  const newDate = props.date.startOf('year').quarter(quarter)
  if (props.selectionMode === 'range') {
    if (!props.rangeState.selecting) {
      emit('pick', { minDate: newDate, maxDate: null })
      emit('select', true)
    } else {
      if (props.minDate && newDate >= props.minDate) {
        emit('pick', { minDate: props.minDate, maxDate: newDate })
      } else {
        emit('pick', { minDate: newDate, maxDate: props.minDate })
      }
      emit('select', false)
    }
  } else {
    emit('pick', quarter)
  }
}

watch(
  () => props.date,
  async () => {
    if (tbodyRef.value?.contains(document.activeElement)) {
      await nextTick()
      currentCellRef.value?.focus()
    }
  }
)

defineExpose({
  /**
   * @description focus current cell
   */
  focus,
})
</script>
